import { Center, Square } from '@chakra-ui/react';
import { ResponsivePie } from '@nivo/pie';
import React from 'react';

import { PieDatum } from '@/domain/models/Chart';
import {
  ChartDataDomainService,
  CurrencyDomainService,
} from '@/domain/services';

import { YAxisFormat } from '.';

interface Props {
  data: PieDatum[];
  format: YAxisFormat;
  isDisableLegends?: boolean;
}

interface PieCustomLayerProps {
  dataWithArc: PieDatum[];
  centerX: number;
  centerY: number;
  radius: number;
  innerRadius: number;
}
const { priceNotationFormat } = CurrencyDomainService;

const CenteredMetric = (format: YAxisFormat) =>
  function CenteredMetricComponent({
    dataWithArc,
    centerX,
    centerY,
  }: PieCustomLayerProps) {
    const totalPrice = ChartDataDomainService.totalPrice(dataWithArc);
    const yFormat = (value: number) => {
      switch (format) {
        case 'currency':
          return priceNotationFormat(Number(value));
        case 'number':
          return value;
      }
    };

    return (
      <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: '1.2rem',
          fontWeight: 'bold',
        }}
      >
        {yFormat(totalPrice)}
      </text>
    );
  };

export const PieChart: React.FC<Props> = ({
  data,
  format,
  isDisableLegends,
}) => {
  const commonProperties = {
    margin: isDisableLegends
      ? { top: 0, right: 0, bottom: 0, left: 0 }
      : { top: 20, right: 150, bottom: 20, left: 0 },
    animate: true,
  };
  const totalPrice = ChartDataDomainService.totalPrice(data);

  return (
    <ResponsivePie
      {...commonProperties}
      innerRadius={0.6}
      padAngle={0.5}
      cornerRadius={5}
      enableRadialLabels={!isDisableLegends}
      radialLabelsLinkColor={{
        from: 'color',
      }}
      radialLabel={(datum) => {
        const label = datum.label.toString();
        const maxLength = 8;
        const isTruncated = label.length > maxLength;
        return `${label.slice(0, maxLength)}${isTruncated ? '...' : ''}`;
      }}
      radialLabelsLinkStrokeWidth={3}
      radialLabelsLinkOffset={-10}
      radialLabelsLinkHorizontalLength={15}
      radialLabelsTextColor={{
        from: 'color',
        modifiers: [['darker', 1.2]],
      }}
      radialLabelsSkipAngle={25}
      sliceLabel={(datum) => {
        if (isDisableLegends) {
          const label = datum.label.toString();
          const maxLength = 5;
          const isTruncated = label.length > maxLength;
          return `${label.slice(0, maxLength)}${isTruncated ? '...' : ''}`;
        }

        const value = datum.data.value;
        return format === 'currency' ? priceNotationFormat(value) : value;
      }}
      sliceLabelsSkipAngle={25}
      layers={[
        'slices',
        'radialLabels',
        'sliceLabels',
        'legends',
        CenteredMetric(format),
      ]}
      data={data}
      sortByValue={true}
      legends={
        isDisableLegends
          ? []
          : [
              {
                anchor: 'top-right',
                direction: 'column',
                translateX: 150,
                translateY: -20,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: '#333',
                itemBackground: '#fff',
                symbolShape: 'circle',
              },
            ]
      }
      tooltip={({ datum }) => {
        const tagAndValue = (() => {
          const itemTag = datum.id;
          const value = datum.value;
          const itemValue =
            format === 'currency'
              ? `${Math.round((value / totalPrice) * 100)}%`
              : `${value}個`;
          const formatTagAndValue = `${itemTag}:${itemValue}`;
          if (isDisableLegends) {
            return `${formatTagAndValue} ${priceNotationFormat(value)}`;
          }
          return formatTagAndValue;
        })();
        return (
          <Center bg="white" p={2} rounded="sm" shadow="md" fontSize="sm">
            {/*
             // @ts-ignore TS2590 */}
            <Square size="1rem" bg={datum.color} mr={2} />
            {tagAndValue}
          </Center>
        );
      }}
    />
  );
};
